
import { Component, Vue, Prop } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { DateTime } from 'luxon';
import { decodeString } from '@/utils/decoding';
import { PERMISSION } from '@/permissions';
import IconButton from '@/components/shared/suites/IconButton.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';

@Component<InquiryRequestDetails>({
	components: {
		IconButton,
		TextButton
	}
})
export default class InquiryRequestDetails extends Vue {
	@Getter('getRestaurant', { namespace: 'auth' }) private restaurant!: Restaurant;
	@Getter('getTimezone', { namespace: 'auth' }) private timezone!: string;
	@Getter('hasExtraPaymentMethodsConfigured', { namespace: 'auth' }) private hasExtraPaymentMethodsConfigured!: boolean;
	@Getter('hasPermission', { namespace: 'auth' }) private hasPermission!: (permission: PERMISSION) => boolean;
	@Prop({ type: Object, required: false, default: null }) private request!: InquiryFormRequest | null;

	private questionIcons = {
		'select_one': 'mdi-form-dropdown',
		'checkbox': 'mdi-checkbox-marked-outline',
		'timepicker': 'mdi-clock',
		'open': 'mdi-pencil-box-outline',
		'select_multiple': 'mdi-form-dropdown',
		undefined: 'mdi-information'
	};

	private get formattedSubmittedDate(): string {
		if (!this.request) {
			return '';
		}
		const dt = DateTime.fromISO(this.request.created_at);
		return `${dt.toFormat('yyyy/LL/dd')} ${dt.setLocale(this.$i18n.locale).toLocaleString(DateTime.TIME_SIMPLE)}`;
	}

	private get formattedEventDate(): string {
		return this.request ? DateTime.fromISO(this.request.event_date).toFormat('yyyy/LL/dd') : '';
	}

	private get emailSubjectLine(): string {
		return encodeURIComponent(this.$t('orders.inquiries.email_subject', { locationName: decodeString(this.restaurant.display_name || this.restaurant.name) }));
	}

	private get showQuoteButton(): boolean {
		return this.hasPermission(PERMISSION.ADMIN_ONLY) || this.hasExtraPaymentMethodsConfigured;
	}

	/**
	 * Return the Vuetify color name for a given order status.
	 *
	 * @param {string} status
	 * @return {string}
	 */
	private canModifyQuote(status: string): boolean {
		return status === 'pending' || status === 'in-progress';
	}

	/**
	 * Determine the display status for a quote, treating pending quotes past expiry as expired.
	 *
	 * @param {Order} order
	 * @return {string}
	 */
	private getQuoteDisplayStatus(order: InquiryFormRequestQuote): string {
		if (order.status === 'pending' && order.quoteExpiresAt) {
			const expiresAt = DateTime.fromISO(order.quoteExpiresAt, { zone: this.timezone }).endOf('day');
			if (expiresAt < DateTime.now()) {
				return 'expired';
			}
		}
		return order.status;
	}

	private statusColor(status: string): string {
		const colors: Record<string, string> = {
			'in-progress': 'statusGreen',
			pending: 'statusYellow',
			cancelled: 'statusRed',
			expired: 'statusRed'
		};
		return colors[status] || 'primaryOpacity25';
	}

	/**
	 * Format a date string into a localized date and time.
	 *
	 * @param {string} date
	 * @return {string}
	 */
	private formatCreatedDate(date: string): string {
		return `${DateTime.fromISO(date).toFormat('yyyy/LL/dd')} ${DateTime.fromISO(date).setLocale(this.$i18n.locale).toLocaleString(DateTime.TIME_SIMPLE)}`;
	}
}
